/* ------------------------------------------------------------------ *
 * Filter data model for the Knowledge sections (Reports / Publications
 * / Research etc). Mirrors the same shape used on the Newsroom listing
 * (data/News.js) — keyword, categories, tags, date — but field-names
 * are read defensively since each Knowledge item type may not carry
 * exactly the same keys.
 *
 * Expected-ish item shape (all optional, all fall back gracefully):
 *   {
 *     id, title, description,
 *     category | type,      // e.g. "Report", "Whitepaper"
 *     tags | subjects,      // string[]
 *     date | dateISO | publishedAt | updatedAt
 *   }
 * ------------------------------------------------------------------ */

export const EMPTY_FILTERS = {
  keyword: "",
  categories: [],
  tags: [],
  date: "",
};

export const DATE_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "1y", label: "This year" },
];

export function getItemCategory(item) {
  return item.category || item.type || "General";
}

export function getItemTags(item) {
  return item.tags || item.subjects || [];
}

export function getItemDateISO(item) {
  const raw = item.dateISO || item.date || item.publishedAt || item.updatedAt;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

function withinRange(dateISO, range) {
  if (!dateISO) return false;
  const now = new Date();
  const d = new Date(dateISO);
  const diffDays = (now - d) / (1000 * 60 * 60 * 24);
  switch (range) {
    case "7d":
      return diffDays >= 0 && diffDays <= 7;
    case "30d":
      return diffDays >= 0 && diffDays <= 30;
    case "3m":
      return diffDays >= 0 && diffDays <= 90;
    case "6m":
      return diffDays >= 0 && diffDays <= 180;
    case "1y":
      return d.getFullYear() === now.getFullYear();
    default:
      return true;
  }
}

export function getUniqueCategories(items) {
  return [...new Set(items.map(getItemCategory).filter(Boolean))];
}

export function getUniqueTags(items) {
  return [...new Set(items.flatMap(getItemTags).filter(Boolean))];
}

export function countActive(filters) {
  return (
    (filters.keyword.trim() ? 1 : 0) +
    filters.categories.length +
    filters.tags.length +
    (filters.date ? 1 : 0)
  );
}

export function applyFilters(items, filters) {
  return items.filter((item) => {
    const category = getItemCategory(item);
    const tags = getItemTags(item);
    const dateISO = getItemDateISO(item);

    if (filters.keyword.trim()) {
      const q = filters.keyword.trim().toLowerCase();
      const hay = `${item.title || ""} ${item.description || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }

    if (filters.categories.length && !filters.categories.includes(category)) {
      return false;
    }

    if (filters.tags.length && !filters.tags.some((t) => tags.includes(t))) {
      return false;
    }

    if (filters.date && !withinRange(dateISO, filters.date)) {
      return false;
    }

    return true;
  });
}